import { DataTableEntry } from '@boltenergy-be/design-system'
import { DataTableRow } from '@boltenergy-be/design-system/dist/components/data-table/types'
import { CustomerAccountingDocument, CustomerInvoice, CustomerPayment, Language } from '@boltenergy-be/shared'

export enum InvoiceTableTabs {
  ALL_INVOICES = 'allInvoices',
  OUTSTANDING_INVOICES = 'outstandingInvoices',
  ALL_TRANSACTIONS = 'allTransactions',
  SUPER_USER_CUSTOMER = 'superUserCustomer',
  SUPER_USER_SUPPLIER = 'superUserSupplier'
}

export type useInvoicesTableProps = {
  tab: InvoiceTableTabs
  selectedDocumentId?: CustomerAccountingDocument['id'] | null
}

export type filteredInvoicesProps = {
  tab: useInvoicesTableProps['tab']
  documents: CustomerAccountingDocument[]
}

export type FilterOutDirectDebitBounceProps = {
  documents: CustomerAccountingDocument[]
  tab: useInvoicesTableProps['tab']
}

export type InvoicesResponse = {
  data: InvoiceTableDataRow[]
  selectedRow?: InvoiceTableDataRow
  isLoading: boolean
  isAlsoSupplier: boolean
}

// TABLE DATA

export enum InvoiceCells {
  ADDRESS = 'address',
  INVOICE_NUMBER = 'invoiceNumber',
  DATE = 'date',
  DOCUMENT_TYPE = 'documentType',
  AMOUNT = 'amount',
  STATUS = 'status',
  PAYMENT_ACTION = 'paymentAction',
  DOWNLOAD_ACTION = 'downloadAction',
  RUNNING_BALANCE = 'runningBalance',
  MATCHING_NUMBER = 'matchingNumber',
  PAYMENT_METHOD = 'paymentMethod',
  OPTIONS = 'options'
}

export type CommonCells = InvoiceCells.DATE | InvoiceCells.DOCUMENT_TYPE | InvoiceCells.AMOUNT | InvoiceCells.STATUS | InvoiceCells.ADDRESS
export type RegularTableCells = CommonCells | InvoiceCells.INVOICE_NUMBER | InvoiceCells.PAYMENT_ACTION | InvoiceCells.DOWNLOAD_ACTION

export type SuperUserTableCells =
  | CommonCells
  | InvoiceCells.INVOICE_NUMBER
  | InvoiceCells.RUNNING_BALANCE
  | InvoiceCells.MATCHING_NUMBER
  | InvoiceCells.PAYMENT_METHOD
  | InvoiceCells.OPTIONS

export type TransactionRows = CommonCells

export type InvoiceTableCellEnums = RegularTableCells | SuperUserTableCells | TransactionRows

/** Maps every invoice cell to the type of its rawData, so entries give type feedback instead of `unknown` */
export type InvoiceCellRawData = {
  [InvoiceCells.ADDRESS]: string | null
  [InvoiceCells.INVOICE_NUMBER]: CustomerInvoice['invoiceNumber'] | null
  [InvoiceCells.DATE]: string
  [InvoiceCells.DOCUMENT_TYPE]: CustomerPayment['documentType'] | CustomerInvoice['invoiceType']
  [InvoiceCells.AMOUNT]: number
  [InvoiceCells.STATUS]: string
  [InvoiceCells.PAYMENT_ACTION]: CustomerInvoice['paymentUrl']
  [InvoiceCells.DOWNLOAD_ACTION]: { pdf: CustomerInvoice['pdfUrl']; xml?: CustomerInvoice['peppolUrl'] }
  [InvoiceCells.RUNNING_BALANCE]: number
  [InvoiceCells.MATCHING_NUMBER]: number
  [InvoiceCells.PAYMENT_METHOD]: null
  [InvoiceCells.OPTIONS]: null
}

export type InvoiceTableEntry<Cell extends InvoiceCells> = Omit<DataTableEntry<InvoiceCells>, 'rawData'> & {
  rawData?: InvoiceCellRawData[Cell]
}

export type InvoiceTableDataRow<Cells extends InvoiceTableCellEnums | InvoiceCells = InvoiceCells> = Omit<
  DataTableRow<Cells>,
  'entries'
> & {
  entries: { [Cell in Cells]?: InvoiceTableEntry<Cell> }
}

export type GetTableData<Document extends CustomerInvoice | CustomerPayment> = {
  documents: Document[]
  isTabletOrMobile: boolean
  language: Language
  showAddress?: boolean
  usesDirectDebit: boolean
}
